<template>
  <div class="pl-2">
    <div class="mb-6 relative w-full md:h-[400px] h-[460px] pr-2">
      <YandexMap
        v-model="map"
        class="ring-1 overflow-hidden rounded-md"
        @click="selectedItemId = null"
        :settings="{
          location: {
            center: [center.longitude, center.latitude],
            zoom: 13,
          },
          theme: theme,
          zoomRange: {
            min: 6,
            max: 18,
          },
        }"
        :real-settings-location="false"
      >
        <YandexMapClusterer
          :grid-size="64"
          :zoom-on-cluster-click="{
            disableMinimalZoomCorrectDiff: true,
          }"
          :settings="{
            hasHint: false,
            hasBalloon: false,
            maxZoom: 14,
          }"
          :cluster-marker-props="{
            hideOutsideViewport: false,
          }"
        >
          <YandexMapMarker
            v-for="(item, i) in options"
            :position="'left-center top'"
            :settings="{
              coordinates: [
                item.coordinates.longitude,
                item.coordinates.latitude,
              ],
              hideOutsideViewport: false,
            }"
            class="cursor-pointer"
            @click.prevent.stop="setMarker(item)"
          >
            <div
              class="group relative text-primary"
              :class="{
                'text-primary z-20 relative': selectedItemId === item.id,
                'text-slate-300 dark:text-slate-500':
                  !!selectedItemId && selectedItemId !== item.id,
              }"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                width="36"
                height="41"
                viewBox="0 0 36 41"
              >
                <path
                  fill="currentColor"
                  d="M30.728 5.272a18 18 0 0 1 .406 25.038l-.406.42-8.486 8.484a6 6 0 0 1-8.194.27l-.288-.27-8.488-8.486A18 18 0 0 1 30.728 5.272ZM18 12a6 6 0 1 0 0 12.002A6 6 0 0 0 18 12Z"
                />
              </svg>
            </div>
          </YandexMapMarker>

          <template #cluster="context">
            <DefaultClusterMarker :length="context.length" />
          </template>
        </YandexMapClusterer>

        <YandexMapDefaultFeaturesLayer />
        <YandexMapDefaultSchemeLayer
          :settings="theme === 'dark' ? { customization } : {}"
        />
      </YandexMap>

      <Transition v-bind="fade">
        <div
          v-if="selectedItem"
          class="flex flex-col absolute left-10 md:left-0 bottom-0 dark:bg-primary-1000 bg-white rounded-tr-md p-4 md:w-[260px] right-12 md:right-auto ring-1"
        >
          <IconX
            @click="selectedItemId = null"
            class="absolute -top-6 -right-6 w-5 h-5 cursor-pointer hidden md:block"
          />
          <figure
            v-if="selectedItem.image"
            class="md:h-[200px] h-[140px] w-full mx-auto rounded-md overflow-hidden mb-4"
          >
            <img
              :src="selectedItem.image"
              alt="point image"
              class="object-cover object-center w-full h-full"
            />
          </figure>

          <div
            v-text="selectedItem.title"
            class="md:text-sm text-xs dark:text-white/80 text-black/80 mb-4"
          />

          <UIButton title="Выбрать" size="sm" @click="selectFromMap" />
        </div>
      </Transition>
    </div>
    <div>
      <FormKit
        type="text"
        v-model="search"
        placeholder="Введите текст"
        :classes="{ outer: 'pr-2' }"
      />
      <div
        ref="itemsRef"
        class="max-h-[240px] overflow-y-auto scrollbar-w-2 scrollbar-thumb-rounded-3xl scrollbar scrollbar-thumb-slate-700 dark:scrollbar-thumb-slate-100 scrollbar-track-transparent pr-3"
      >
        <ul class="space-y-[6px]">
          <li
            v-for="(item, i) in filteredOptions.map((i) => i.item)"
            :key="item.id"
            :ref="setItemRef"
            @click="setMarker(item)"
          >
            <label
              class="flex items-center relative bg-black-50 dark:bg-white/10 rounded px-4 py-3"
              :class="[
                {
                  'opacity-50':
                    (inputType === 'checkbox' &&
                      !model.includes(i) &&
                      passed) ||
                    (inputType === 'radio' && passed),
                },
                { 'cursor-pointer': !passed },
              ]"
            >
              <div
                class="w-6 h-6 flex border-2 border-primary bg-white dark:bg-white/10 mr-3 flex-none z-10"
                :class="[
                  inputType === 'checkbox' ? 'rounded-md' : 'rounded-full',
                ]"
              >
                <IconCheck
                  v-if="inputType === 'checkbox' && model.includes(item.id)"
                  class="size-4 m-auto stroke-[6px]"
                  :class="
                    model.includes(item.id) ? 'text-primary' : 'text-white'
                  "
                />
                <div
                  class="w-3 h-3 m-auto rounded-full bg-primary"
                  v-if="inputType === 'radio' && model === item.id"
                />
              </div>
              <div class="font-medium pr-12" v-html="item.title" />
              <input
                v-model="model"
                class="absolute opacity-0"
                :disabled="passed"
                :type="inputType"
                :value="item.id"
              />
            </label>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useWindowSize } from '@vueuse/core'
import { useFuse } from '@vueuse/integrations/useFuse'
import { find, findIndex, isNumber, shuffle } from 'lodash-es'
import {
  computed,
  inject,
  onBeforeUpdate,
  onMounted,
  ref,
  shallowRef,
  watch,
} from 'vue'
import {
  YandexMap,
  YandexMapClusterer,
  YandexMapDefaultFeaturesLayer,
  YandexMapDefaultSchemeLayer,
  YandexMapMarker,
} from 'vue-yandex-maps'

import { IconCheck, IconX } from '@tabler/icons-vue'

import ImagePlaceholder from '@/components/ImagePlaceholder.vue'
import DefaultClusterMarker from '@/components/maps/DefaultClusterMarker.vue'
import DefaultMarker from '@/components/maps/DefaultMarker.vue'
import UIButton from '@/components/ui/UIButton.vue'
import { calculcateCenterPoint } from '@/utils/global.js'
import { fade } from '@/utils/transitions.js'

const props = defineProps({
  context: {
    type: Object,
    default: {},
  },
  modelValue: {
    type: [Array, Object, String, Number],
  },
  passed: {
    type: Boolean,
  },
  current: {
    type: Boolean,
  },
  accept: {
    default: () => null,
  },
})

const map = shallowRef(null)
const itemRefs = ref([])
const itemsRef = ref(null)

const selectedItemId = ref(null)

const selectedItem = computed(() =>
  find(options.value, ['id', selectedItemId.value]),
)

const theme = inject('theme')

const search = ref(null)

const inputType = ref(props.context.multiple ? 'checkbox' : 'radio')

const customization = [
  {
    tags: {
      any: ['water'],
    },
    elements: 'geometry',
    stylers: [
      {
        color: '#1b2136',
      },
    ],
  },
]

const options = computed(() => {
  return (
    props.context.shuffle
      ? shuffle(props.context.options)
      : props.context.options
  ).map((i) => {
    i.title = i.title.replace('Мурманская обл,', '')
    return i
  })
})

const center = calculcateCenterPoint(options.value)

const { results: filteredOptions } = useFuse(search, options, {
  fuseOptions: {
    shouldSort: true,
    threshold: 0.6,
    keys: ['title'],
  },
  matchAllWhenSearchEmpty: true,
})

const other = ref('')
const emits = defineEmits(['passed', 'update:model-value'])

const model = computed({
  get: () => {
    return props.modelValue ?? []
  },
  set: (val) => {
    emits('update:model-value', val)
  },
})

function setItemRef(el) {
  if (el) {
    itemRefs.value.push(el)
  }
}

function selectFromMap() {
  const i = findIndex(
    filteredOptions.value,
    (i) => i.item.id === selectedItemId.value,
  )

  if (i !== -1) {
    itemsRef.value.scrollTo({
      top:
        itemRefs.value[i].offsetTop -
        itemsRef.value.offsetTop -
        itemRefs.value[i].clientHeight,
      behavior: 'smooth',
    })

    model.value = selectedItemId.value
  }
}

function setMarker(item) {
  selectedItemId.value = item.id

  if (map.value.zoom < 16) {
    map.value?.setLocation({
      zoom: 16,
      duration: 0,
    })
  }

  const coords = calculatePointInTwoThirds(item.coordinates, map.value.zoom)

  map.value?.setLocation({
    center: [coords.longitude, coords.latitude],
    duration: 500,
  })
}

const { width } = useWindowSize()

function calculatePointInTwoThirds(coords, zoom) {
  const b = map.value.bounds

  const deltaX = b[0][0] - b[1][0]
  const deltaY = b[0][1] - b[1][1]

  if (width.value < 640) {
    return {
      latitude: coords.latitude - deltaY / 4,
      longitude: coords.longitude + deltaX / 20,
    }
  }

  return {
    latitude: coords.latitude + (deltaY / 12) * (1 + 1 / zoom),
    longitude: coords.longitude + (deltaX / 7) * (1 + 1 / zoom),
  }
}

onBeforeUpdate(() => {
  itemRefs.value = []
})
</script>
